// Pure helpers for the Home Learning page: which topics are practice exams, and how a topic's
// lessons group into year-group sections.

// Diagnostic topics carry codes like `diagnostic-y4` (api/src/onboarding/diagnostic-assignment.service.ts).
const PRACTICE_EXAM_CODE_PREFIX = 'diagnostic-';

export const OTHER_YEAR_GROUP = 'Other lessons';

export function isPracticeExamTopic(topic) {
  return typeof topic?.code === 'string' && topic.code.startsWith(PRACTICE_EXAM_CODE_PREFIX);
}

/** The topic's lessons a child may start, in the admin-set order. */
export function visibleLessons(topic) {
  return (topic?.lessons ?? [])
    .filter((lesson) => lesson.lessonId?.lessonVisible)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function lessonYearGroups(lesson) {
  const { yearGroupLevels, yearGroupLevel } = lesson.lessonId;
  if (yearGroupLevels?.length) return yearGroupLevels;
  return yearGroupLevel ? [yearGroupLevel] : [];
}

function yearGroupRank(label) {
  if (label === OTHER_YEAR_GROUP) return Number.MAX_SAFE_INTEGER;
  const year = label.match(/\d+/);
  return year ? Number(year[0]) : Number.MAX_SAFE_INTEGER - 1;
}

/**
 * Groups visible lessons by year group, youngest first. A lesson tagged with several year groups
 * appears under each; a lesson with none lands in "Other lessons" at the end.
 */
export function groupLessonsByYear(topic) {
  const groups = new Map();
  visibleLessons(topic).forEach((lesson) => {
    const years = lessonYearGroups(lesson);
    (years.length ? years : [OTHER_YEAR_GROUP]).forEach((year) => {
      if (!groups.has(year)) groups.set(year, []);
      groups.get(year).push(lesson);
    });
  });
  return [...groups.entries()]
    .map(([yearGroup, lessons]) => ({ yearGroup, lessons }))
    .sort((a, b) => yearGroupRank(a.yearGroup) - yearGroupRank(b.yearGroup) || a.yearGroup.localeCompare(b.yearGroup));
}

/** The lessons a topic block measures progress on: the child's own year group when the topic has one, else all. */
export function lessonsForChild(topic, childYearGroup) {
  return groupLessonsByYear(topic).find((group) => group.yearGroup === childYearGroup)?.lessons ?? visibleLessons(topic);
}

/**
 * Progress over some lessons from the Home Learning rates (`rates`: lessonId → { band }). Each lesson's band is
 * grey (not tried), red, amber or green; a lesson without a rate counts as grey. Null when there are no rates
 * (the rates are off or still loading), so the page shows no progress rather than a false "nothing done".
 */
export function progressFor(lessons, rates) {
  if (!rates) return null;
  const bands = lessons.map((lesson) => rates[lesson.lessonId._id]?.band ?? 'grey');
  return { bands, total: bands.length, mastered: bands.filter((band) => band === 'green').length };
}
